"use client";
import React from "react";
import { Box, Container, Typography, Button, Card, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";
const GradientBackground = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #8871E6 0%, #7F59B7 50%, #502C85 100%)",
  padding: "80px 20px",
  display: "flex",
  alignItems: "center",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: { padding: "60px 15px" },
  [theme.breakpoints.down("sm")]: { padding: "40px 10px" },
}));

const BadgeChip = styled(Chip)({
  background: "transparent",
  border: "1px solid #DDE4E8",
  color: "#E0E0E0",
  fontFamily: "var(--font-plusjakarta)",
  borderRadius: "24px",
  padding: "20px 3px",
  fontSize: "14px",
  fontWeight: 400,
});

const FeatureCard = styled(Card)(({ theme }) => ({
  background: "#FFFFFF1A",
  backdropFilter: "blur(10px)",
  border: "1px solid #FFFFFF33",
  borderRadius: "24px",
  height: "100%",
  minHeight: "260px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "32px 24px",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  cursor: "pointer",
  "&:hover": { transform: "translateY(-8px)", boxShadow: "none" },
  [theme.breakpoints.down("sm")]: { padding: "24px 16px" },
}));

const IconContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "20px",
  width: "100%",
});

const CTAButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#000",
  borderRadius: "24px",
  padding: "12px 25px",
  textTransform: "none",
  fontSize: "17px",
  fontWeight: 400,
  fontFamily: "var(--font-outfit)",
  marginTop: ".5%",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    background: "#000",
    color: "#fff",
    transform: "scale(1.05) translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: { fontSize: "15px", padding: "10px 20px" },
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  color: "#D5BEF7",
  fontSize: "21px",
  lineHeight: 1.3,
  maxWidth: "1200px",
  marginBottom: "48px",
  fontFamily: "var(--font-outfit)",
  fontWeight: 300,
  marginLeft: ".5%",
  [theme.breakpoints.down("md")]: {
    fontSize: "18px",
    textAlign: "left",
    marginLeft: "0",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    marginLeft: "0",
    textAlign: "left",
  },
}));

const DisclaimerText = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "17px",
  lineHeight: 1.3,
  marginTop: "30px",
  fontFamily: "var(--font-outfit)",
  fontWeight: 300,
  marginLeft: ".5%",
  "& strong": { fontWeight: 700 },
  [theme.breakpoints.down("md")]: {
    fontSize: "15px",
    marginLeft: "0",
    textAlign: "left",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    marginLeft: "0",
    textAlign: "left",
  },
}));

const featureImages = [
  IMAGES.SECURITY_IMG_1,
  IMAGES.SECURITY_IMG_2,
  IMAGES.SECURITY_IMG_3,
  IMAGES.SECURITY_IMG_4,
  IMAGES.SECURITY_IMG_5,
  IMAGES.SECURITY_IMG_6,
];

export default function SecurityFeatures() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const router = useRouter();

  const featuresRaw = t("securityFeatures.features", { returnObjects: true });
  const features = Array.isArray(featuresRaw) ? featuresRaw : [];

  return (
    <GradientBackground sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      <Container maxWidth="xl">
        <Box sx={{ position: "relative" }}>
          {/* Badge */}
          <Box sx={{ mb: 2 }}>
            <BadgeChip label={t("securityFeatures.chip")} />
          </Box>

          {/* Header with Button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              mb: 1,
              gap: 2,
            }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                color: "#FFFFFF",
                fontWeight: 300,
                fontSize: {
                  xs: "28px",
                  sm: "32px",
                  md: "48px",
                  lg: "48px",
                  xl: "56px",
                },
                lineHeight: 1.2,
                fontFamily: "var(--font-outfit)",
                flex: 1,
                textAlign: isArabic ? "right" : "left",
                ml: isArabic ? "auto" : 0, // ← left margin hata do Arabic mein
                mr: isArabic ? 0 : "auto",

                mb: { xs: 2, sm: 2, md: 0 },
              }}
            >
              {t("securityFeatures.title1")}{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  marginRight: isArabic ? "0" : "1%",
                  marginLeft: isArabic ? "1%" : "0",
                }}
              >
                {t("securityFeatures.titleHighlight")}
              </Box>
              {t("securityFeatures.title2")}
            </Typography>

            <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
              <CTAButton
                component="a"
                endIcon={
                  <ArrowForwardIcon
                    sx={{
                      transform: isArabic ? "scaleX(-1)" : "none",
                      mr: isArabic ? "50%" : 0,
                      mt: isArabic ? "5%" : 0,
                    }}
                  />
                }
              >
                {t("securityFeatures.cta")}
              </CTAButton>
            </NextLink>
          </Box>

          {/* Description */}
          <DescriptionText sx={{ textAlign: isArabic ? "right" : "left" }}>
            {t("securityFeatures.desc")}
          </DescriptionText>

          {/* Feature Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(6, 1fr)",
              },
              gap: 3,
            }}
          >
            {features.map((feature, index) => (
              <FeatureCard key={index} elevation={0}>
                <IconContainer>
                  <Image
                    src={featureImages[index]}
                    alt={feature.title}
                    width={180}
                    height={150}
                    style={{ objectFit: "contain" }}
                  />
                </IconContainer>
                <Box
                  sx={{ width: "100%", textAlign: isArabic ? "right" : "left" }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      fontWeight: 500,
                      fontSize: "18px",
                      lineHeight: 1.4,
                      mb: 1,
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#D5BEF7",
                      fontSize: "15px",
                      lineHeight: 1.3,
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </FeatureCard>
            ))}
          </Box>

          {/* Disclaimer */}
          <DisclaimerText sx={{ textAlign: isArabic ? "right" : "left" }}>
            <strong>P.S.</strong> {t("securityFeatures.disclaimer")}
          </DisclaimerText>
        </Box>
      </Container>
    </GradientBackground>
  );
}
